import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import CircularProgress from '@mui/material/CircularProgress';
import Popover from '@mui/material/Popover';
import {
  forwardRef,
  type KeyboardEvent,
  type UIEvent,
  useCallback,
  useId,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { controlHeight, radius } from '@/shared/theme';
import { Icon } from './icon';
import { SearchField } from './search-field';

/** Keeps up to 8 digits and renders them as DD.MM.YYYY while typing. */
export function maskDate(raw: string) {
  const digits = raw.replace(/\D/g, '').slice(0, 8);
  let out = digits.slice(0, 2);
  if (digits.length > 2) out += `.${digits.slice(2, 4)}`;
  if (digits.length > 4) out += `.${digits.slice(4, 8)}`;
  return out;
}

type TypedResult =
  | { ok: true; value: string }
  | { ok: false; error: 'incomplete' | 'invalid' | 'mustBePast' };

/** Validates a typed DD.MM.YYYY (must be a real, past date) and returns it as DD-MM-YYYY. */
export function parseTypedDate(text: string, today = new Date()): TypedResult {
  const digits = text.replace(/\D/g, '');
  if (digits.length === 0) return { ok: true, value: '' };
  if (digits.length < 8) return { ok: false, error: 'incomplete' };
  const d = Number(digits.slice(0, 2));
  const m = Number(digits.slice(2, 4));
  const y = Number(digits.slice(4, 8));
  const date = new Date(y, m - 1, d);
  if (date.getDate() !== d || date.getMonth() !== m - 1 || date.getFullYear() !== y) {
    return { ok: false, error: 'invalid' };
  }
  const start = new Date(today);
  start.setHours(0, 0, 0, 0);
  if (date.getTime() >= start.getTime()) return { ok: false, error: 'mustBePast' };
  return { ok: true, value: `${digits.slice(0, 2)}-${digits.slice(2, 4)}-${digits.slice(4, 8)}` };
}

export interface AvailableDatePickerHandle {
  /** Commits text typed but not yet confirmed; false (with an inline error) if it is invalid. */
  commit: () => boolean;
}

interface AvailableDatePickerProps {
  label: string;
  /** DD-MM-YYYY or '' */
  value: string;
  onChange: (value: string) => void;
  /** Days with data, DD-MM-YYYY, newest first. */
  dates: string[];
  loading?: boolean;
  hasMore?: boolean;
  loadingMore?: boolean;
  onLoadMore?: () => void;
  /** Server-side search; receives the masked search text. */
  onSearch?: (term: string) => void;
  searching?: boolean;
}

/**
 * Date field for "days with data": type DD.MM.YYYY directly, or pick from a searchable list that
 * loads more entries on scroll (production DateSearchSelector, prototype styling).
 */
export const AvailableDatePicker = forwardRef<AvailableDatePickerHandle, AvailableDatePickerProps>(
  function AvailableDatePicker(
    {
      label,
      value,
      onChange,
      dates,
      loading,
      hasMore,
      loadingMore,
      onLoadMore,
      onSearch,
      searching,
    },
    ref,
  ) {
    const { t } = useTranslation();
    const id = useId();
    const anchor = useRef<HTMLDivElement>(null);
    const [open, setOpen] = useState(false);
    const [typed, setTyped] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [search, setSearch] = useState('');

    const display = typed ?? value.replaceAll('-', '.');

    const commit = useCallback((): boolean => {
      if (typed === null) return true;
      const result = parseTypedDate(typed);
      if (!result.ok) {
        setError(t(`datePicker.${result.error}`));
        return false;
      }
      onChange(result.value);
      setTyped(null);
      setError(null);
      return true;
    }, [typed, onChange, t]);

    useImperativeHandle(ref, () => ({ commit }), [commit]);

    const updateSearch = (next: string) => {
      const masked = maskDate(next);
      setSearch(masked);
      onSearch?.(masked);
    };

    const close = () => {
      setOpen(false);
      if (search) updateSearch('');
    };

    const pick = (date: string) => {
      onChange(date);
      setTyped(null);
      setError(null);
      close();
    };

    const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        commit();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        setOpen(true);
      }
    };

    const onListScroll = (e: UIEvent<HTMLDivElement>) => {
      const el = e.currentTarget;
      if (hasMore && !loadingMore && el.scrollHeight - el.scrollTop - el.clientHeight < 60)
        onLoadMore?.();
    };

    return (
      <Box>
        <Box
          component="label"
          htmlFor={id}
          sx={{
            display: 'block',
            fontSize: 11,
            color: 'text.secondary',
            fontWeight: 600,
            mb: '6px',
          }}
        >
          {label}
        </Box>
        <Box
          ref={anchor}
          sx={(theme) => ({
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            height: controlHeight.md,
            width: 176,
            pl: '14px',
            pr: '6px',
            border: `1px solid ${error ? theme.vars.palette.md.bad : theme.vars.palette.md.border}`,
            background: theme.vars.palette.md.panel,
            borderRadius: radius.control,
            transition: 'box-shadow .14s',
            '&:focus-within': {
              borderColor: error ? theme.vars.palette.md.bad : theme.vars.palette.md.primary,
              boxShadow: `0 0 0 4px ${theme.vars.palette.md.primary050}`,
            },
          })}
        >
          <Box sx={(theme) => ({ display: 'flex', color: theme.vars.palette.md.primary })}>
            <Icon name="calendar" size={18} />
          </Box>
          <Box
            component="input"
            id={id}
            value={display}
            placeholder={t('datePicker.placeholder')}
            inputMode="numeric"
            autoComplete="off"
            aria-invalid={!!error || undefined}
            aria-describedby={error ? `${id}-err` : undefined}
            onChange={(e) => {
              setTyped(maskDate(e.target.value));
              setError(null);
            }}
            onBlur={() => {
              if (!open) commit();
            }}
            onKeyDown={onKeyDown}
            sx={(theme) => ({
              flex: 1,
              minWidth: 0,
              border: 0,
              outline: 0,
              background: 'transparent',
              color: theme.vars.palette.md.text,
              font: 'inherit',
              fontSize: 14,
              fontWeight: 600,
              fontVariantNumeric: 'tabular-nums',
            })}
          />
          <ButtonBase
            onClick={() => setOpen((o) => !o)}
            aria-label={t('datePicker.open')}
            aria-expanded={open}
            aria-haspopup="listbox"
            sx={(theme) => ({
              width: 30,
              height: 30,
              borderRadius: radius.sm,
              color: theme.vars.palette.md.muted,
              '&:hover': { background: theme.vars.palette.md.panel2 },
            })}
          >
            <Icon name="chevronDown" size={18} />
          </ButtonBase>
        </Box>
        {error && (
          <Box
            id={`${id}-err`}
            role="alert"
            sx={(theme) => ({
              mt: '5px',
              fontSize: 11.5,
              fontWeight: 600,
              color: theme.vars.palette.md.badInk,
              maxWidth: 176,
            })}
          >
            {error}
          </Box>
        )}

        <Popover
          open={open}
          anchorEl={anchor.current}
          onClose={close}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          slotProps={{ paper: { sx: { width: 290, maxWidth: '92vw', mt: 1, p: 1.5 } } }}
        >
          <SearchField
            value={search}
            onChange={updateSearch}
            placeholder={t('datePicker.search')}
            tinted
            autoFocus
            sx={{ mb: 1.25 }}
          />
          <Box
            role="listbox"
            aria-label={label}
            onScroll={onListScroll}
            sx={{
              maxHeight: 256,
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: '1px',
            }}
          >
            {(loading || searching) && dates.length === 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={20} />
              </Box>
            )}
            {dates.map((date) => {
              const selected = date === value;
              return (
                <ButtonBase
                  key={date}
                  role="option"
                  aria-selected={selected}
                  onClick={() => pick(date)}
                  sx={(theme) => ({
                    justifyContent: 'flex-start',
                    gap: '10px',
                    p: '9px 10px',
                    borderRadius: radius.sm,
                    fontSize: 13.5,
                    fontWeight: 600,
                    fontVariantNumeric: 'tabular-nums',
                    background: selected ? theme.vars.palette.md.primary050 : 'transparent',
                    color: selected ? theme.vars.palette.md.primary : theme.vars.palette.md.text,
                    '&:hover': { background: theme.vars.palette.md.panel2 },
                  })}
                >
                  <Box
                    sx={(theme) => ({
                      display: 'flex',
                      color: selected ? theme.vars.palette.md.primary : theme.vars.palette.md.faint,
                    })}
                  >
                    <Icon name={selected ? 'check' : 'calendar'} size={16} />
                  </Box>
                  {date.replaceAll('-', '.')}
                </ButtonBase>
              );
            })}
            {!loading && !searching && dates.length === 0 && (
              <Box sx={{ p: '12px 10px', fontSize: 13, color: 'text.secondary' }}>
                {t('datePicker.noneFound')}
              </Box>
            )}
            {loadingMore && (
              <Box
                sx={{
                  p: '10px',
                  fontSize: 12,
                  color: 'text.secondary',
                  display: 'flex',
                  gap: 1,
                  alignItems: 'center',
                }}
              >
                <CircularProgress size={14} /> {t('datePicker.loadingMore')}
              </Box>
            )}
          </Box>
        </Popover>
      </Box>
    );
  },
);
